import React, { useEffect, useRef, useState } from 'react';
import { FontWeightEnum, getAmount, TextVariantColorEnum, ShareTypeEnum } from '@app/common';
import { getColor } from '../../tables/utils/get-color';
import { getFakeAmount } from '../../../../utils/get-fake-amount';
import { useRootStore } from '../../../../providers/root-store-provider';
import { getLockedProps, getValueClassName, nonVisibleValueClassName } from '../utils/value-style';

type ValuePercentsCommonProps = {
    mode?: 'styled' | 'pure';
    nullValue?: number | 'hidden';
    amount: number;
    locked?: boolean;
    fractionDigits?: number;
    inParentheses?: boolean;
};

type ValuePercentsStyledProps = {
    mode: 'styled';
    fontWeight?: FontWeightEnum;
    color?: TextVariantColorEnum;
};

type ValuePercentsPureProps = {
    mode?: 'pure';
};

type ValuePercentsProps = ValuePercentsCommonProps & (ValuePercentsStyledProps | ValuePercentsPureProps);

export const ValuePercents: React.FC<ValuePercentsProps> = (props) => {
    const lockedRef = useRef<HTMLSpanElement>(null);
    const rootStore = useRootStore();
    const [blur, setBlur] = useState<number>(0.3);

    const { amount, locked = false, fractionDigits = 2, inParentheses = false, nullValue } = props;

    const mode = props.mode ?? 'styled';

    const fakeAmount = getFakeAmount(amount);
    const valueBefore = getAmount(locked ? fakeAmount : amount, fractionDigits, '%', nullValue);
    const value = inParentheses ? `(${valueBefore})` : valueBefore;

    useEffect(() => {
        if (lockedRef.current) {
            const fontSizeValue = parseFloat(window.getComputedStyle(lockedRef.current).fontSize);
            const blurSize = fontSizeValue / 50;
            const roundedBlurSize = parseFloat(blurSize.toFixed(2));
            setBlur(roundedBlurSize);
        }
    }, []);

    const isHidden =
        !rootStore.cookieService.hasJwtToken() &&
        rootStore.portfolioStore.sharedPortfolio?.shareType !== ShareTypeEnum.Public;

    if (isHidden) {
        return <span className={nonVisibleValueClassName}>*** %</span>;
    }

    if ((amount === 0 || amount === -100) && nullValue === 'hidden') {
        return null;
    }

    if (mode === 'styled') {
        const { color, fontWeight = FontWeightEnum.Medium } = props as ValuePercentsStyledProps;

        return (
            <span className={getValueClassName(color || getColor(amount), fontWeight)}>
                <span ref={lockedRef} {...getLockedProps(locked, blur)}>
                    {value}
                </span>
            </span>
        );
    }

    return locked ? (
        <span ref={lockedRef} {...getLockedProps(true, blur)}>
            {value}
        </span>
    ) : (
        <>{value}</>
    );
};
